import React, { useEffect, useMemo, useState } from 'react';

const imageFileRefs = (files = [], max = 3) => (Array.isArray(files) ? files : [])
  .filter(file => typeof file?.type === 'string' && file.type.startsWith('image/') && (file._id || file.id))
  .slice(0, max)
  .map(file => ({ id: String(file._id || file.id), type: file.type }));

const ActivityMediaPreviews = ({
  activityId,
  files,
  backendService,
  max = 3,
  className = 'activity-media-previews',
  imageClassName = 'activity-media-preview',
}) => {
  const fileKey = useMemo(
    () => imageFileRefs(files, max).map(file => `${file.id}:${file.type}`).join('|'),
    [files, max]
  );
  const refs = useMemo(() => imageFileRefs(files, max), [fileKey, max]); // eslint-disable-line react-hooks/exhaustive-deps
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    const resolvedActivityId = activityId ? String(activityId) : '';
    if (!resolvedActivityId || refs.length === 0 || typeof backendService?.fetchMediaFileUrl !== 'function') {
      setPreviews([]);
      return undefined;
    }

    let active = true;
    const controller = new AbortController();
    const ownedUrls = new Set();
    setPreviews([]);

    Promise.all(refs.map(async (file, index) => {
      try {
        const url = await backendService.fetchMediaFileUrl(resolvedActivityId, file.id, '256px', { signal: controller.signal });
        if (!url) return null;
        if (!active) {
          URL.revokeObjectURL?.(url);
          return null;
        }
        ownedUrls.add(url);
        return { id: file.id, url, index };
      } catch (_) {
        return null;
      }
    })).then(results => {
      if (active) setPreviews(results.filter(Boolean));
    });

    return () => {
      active = false;
      controller.abort();
      ownedUrls.forEach(url => URL.revokeObjectURL?.(url));
    };
  }, [activityId, backendService, fileKey, refs]);

  if (previews.length === 0) return null;

  return (
    <span className={className} aria-label={`${previews.length} uploaded image preview${previews.length === 1 ? '' : 's'}`}>
      {previews.map((preview, index) => (
        <img
          key={preview.id}
          src={preview.url}
          alt={`Upload ${index + 1} of ${previews.length}`}
          className={imageClassName}
          onError={() => {
            URL.revokeObjectURL?.(preview.url);
            setPreviews(current => current.filter(item => item.id !== preview.id));
          }}
        />
      ))}
    </span>
  );
};

export { imageFileRefs };
export default ActivityMediaPreviews;
